import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Pencil, Ban, Check, Circle, Users as UsersIcon } from 'lucide-react';

import { usersApi } from '../lib/usersApi';
import { callCentersApi } from '../lib/callCentersApi';
import { toast, toastSuccess, toastError } from '../lib/toast';
import { ROLES, ROLE_BADGE } from '../lib/routeConfig';
import EmptyState from '../components/common/EmptyState';
import { TableRowSkeleton } from '../components/common/TableSkeleton';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
} from '../components/ui/alert-dialog';
import UserFormDialog from '../components/users/UserFormDialog';

/**
 * /utilisateurs — super_admin only (route guard done by AppLayout).
 *
 * Table columns: ID (User N) / Nom / Gmail / Rôle (pill) / Call Center /
 * Statut (dot + label) / Actions (edit pencil + red prohibit icon).
 */
const ROLE_META = {
    [ROLES.SUPER_ADMIN]: { label: 'Super Admin', color: '#4B3FD8' },
    [ROLES.MANAGER]: { label: 'Manager', color: '#3B82F6' },
    [ROLES.CC_AGENT]: { label: 'Agent CC', color: '#6B7280' },
};

function roleLabel(role) {
    return ROLE_META[role]?.label || role;
}

export default function UsersPage() {
    const { user: sessionUser } = useOutletContext();
    const [items, setItems] = React.useState([]);
    const [callCenters, setCallCenters] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogMode, setDialogMode] = React.useState('create');
    const [dialogUser, setDialogUser] = React.useState(null);

    const [confirmDeactivate, setConfirmDeactivate] = React.useState(null); // {user}
    const [busy, setBusy] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [u, c] = await Promise.all([usersApi.list(), callCentersApi.list()]);
            setItems(u.users || []);
            setCallCenters(c.call_centers || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);
    React.useEffect(() => { load(); }, [load]);

    const openInvite = () => {
        setDialogMode('create');
        setDialogUser(null);
        setDialogOpen(true);
    };
    const openEdit = (u) => {
        setDialogMode('edit');
        setDialogUser(u);
        setDialogOpen(true);
    };

    const submitDialog = async (payload) => {
        if (dialogMode === 'create') {
            const res = await usersApi.create(payload);
            const es = res.email_status || {};
            if (es.sent) {
                toastSuccess(`Utilisateur créé. Email d'invitation envoyé.`);
            } else if (es.reason === 'not_configured') {
                toast.message(`Utilisateur créé. Email d'invitation non envoyé — service email non configuré.`, { duration: 8000, position: 'top-right' });
            } else if (es.reason === 'send_failed') {
                toast.message(`Utilisateur créé. Envoi de l'email d'invitation échoué (${es.error || 'erreur'}).`, { duration: 8000, position: 'top-right' });
            } else {
                toastSuccess('Utilisateur créé.');
            }
        } else {
            await usersApi.update(dialogUser.id, payload);
            toastSuccess('Utilisateur mis à jour.');
        }
        setDialogOpen(false);
        await load();
    };

    const confirmDeactivateNow = async () => {
        if (!confirmDeactivate) return;
        setBusy(true);
        try {
            const res = await usersApi.deactivate(confirmDeactivate.user.id);
            const nSessions = res.revoked_sessions || 0;
            toastSuccess(
                `${confirmDeactivate.user.display_name} désactivé.` +
                (nSessions > 0 ? ` ${nSessions} session(s) révoquée(s).` : '')
            );
            setConfirmDeactivate(null);
            await load();
        } catch (e) {
            toastError(e.message, { duration: 8000 });
            setConfirmDeactivate(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="users-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-[#1A1D2E]">Utilisateurs</h2>
                    <p className="text-sm text-[#6B7280]">Whitelist Nivo — gérez les accès et les rôles</p>
                </div>
                <button
                    type="button"
                    data-testid="user-invite-button"
                    onClick={openInvite}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white text-sm font-semibold hover:opacity-95"
                >
                    <Plus size={16} strokeWidth={2} />
                    Inviter un utilisateur
                </button>
            </div>

            <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)] overflow-hidden">
                <div className="overflow-x-auto">
                    <table data-testid="users-table" className="w-full min-w-[880px] text-sm">
                        <thead>
                            <tr className="bg-[#F5F6FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                                <Th>ID</Th>
                                <Th>Nom</Th>
                                <Th>Gmail</Th>
                                <Th>Rôle</Th>
                                <Th>Call Center assigné</Th>
                                <Th>Statut</Th>
                                <Th className="text-right">Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <TableRowSkeleton rows={5} columns={7} testId="users-skeleton" />
                            )}
                            {!loading && error && (<tr><td colSpan={7} className="text-center py-8 text-[#EF4444]" data-testid="users-error">{error}</td></tr>)}
                            {!loading && !error && items.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="py-2">
                                        <EmptyState
                                            testId="users-empty"
                                            Icon={UsersIcon}
                                            title="Aucun utilisateur"
                                            description="Invitez un premier utilisateur pour l'ajouter à la whitelist Nivo."
                                            action={{ label: 'Inviter un utilisateur', Icon: Plus, onClick: openInvite, testId: 'users-empty-invite' }}
                                        />
                                    </td>
                                </tr>
                            )}
                            {!loading && !error && items.map((u, idx) => {
                                const isSelf = u.id === sessionUser?.id;
                                const roleMeta = ROLE_META[u.role] || { label: u.role, color: '#6B7280' };
                                return (
                                    <tr
                                        key={u.id}
                                        data-testid="user-row"
                                        data-user-id={u.id}
                                        data-user-email={u.email}
                                        className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F6FA]'}
                                    >
                                        <Td className="font-semibold text-[#1A1D2E]" data-testid="user-row-id">User {u.user_number}</Td>
                                        <Td data-testid="user-row-name">
                                            {u.display_name}
                                            {isSelf && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#4B3FD8] font-semibold">Vous</span>}
                                        </Td>
                                        <Td className="text-[#6B7280]" data-testid="user-row-email">{u.email}</Td>
                                        <Td data-testid="user-row-role">
                                            <span
                                                className="inline-flex items-center h-6 px-2 rounded-md text-[11px] font-semibold"
                                                style={{ backgroundColor: `${roleMeta.color}26`, color: roleMeta.color }}
                                            >
                                                {roleMeta.label}
                                            </span>
                                        </Td>
                                        <Td data-testid="user-row-cc">
                                            {u.role === ROLES.CC_AGENT
                                                ? (u.call_center_name || <span className="text-[#EF4444]">— non assigné</span>)
                                                : <span className="text-[#6B7280]">Global (Tous)</span>}
                                        </Td>
                                        <Td data-testid="user-row-status">
                                            <span className="inline-flex items-center gap-1.5">
                                                <span
                                                    className="w-2 h-2 rounded-full"
                                                    style={{ backgroundColor: u.is_active ? '#10B981' : '#EF4444' }}
                                                />
                                                <span className={u.is_active ? 'text-[#10B981]' : 'text-[#EF4444]'}>
                                                    {u.is_active ? 'Actif' : 'Inactif'}
                                                </span>
                                            </span>
                                        </Td>
                                        <Td className="text-right">
                                            <div className="inline-flex items-center gap-1">
                                                <button
                                                    data-testid="user-row-edit"
                                                    onClick={() => openEdit(u)}
                                                    className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-[#F5F6FA] text-[#6B7280] hover:text-[#4B3FD8]"
                                                    aria-label="Modifier l'utilisateur"
                                                >
                                                    <Pencil size={16} strokeWidth={1.75} />
                                                </button>
                                                <button
                                                    data-testid="user-row-deactivate"
                                                    onClick={() => setConfirmDeactivate({ user: u })}
                                                    disabled={!u.is_active || isSelf}
                                                    className="h-8 w-8 inline-flex items-center justify-center rounded text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-30 disabled:cursor-not-allowed"
                                                    aria-label="Désactiver l'utilisateur"
                                                    title={isSelf ? 'Vous ne pouvez pas désactiver votre propre compte' : (!u.is_active ? 'Utilisateur déjà inactif' : 'Désactiver')}
                                                >
                                                    <Ban size={16} strokeWidth={1.75} />
                                                </button>
                                            </div>
                                        </Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <UserFormDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                mode={dialogMode}
                user={dialogUser}
                callCenters={callCenters}
                onSubmit={submitDialog}
                selfId={sessionUser?.id}
            />

            <AlertDialog open={!!confirmDeactivate} onOpenChange={(o) => { if (!o) setConfirmDeactivate(null); }}>
                <AlertDialogContent data-testid="user-deactivate-dialog" className="bg-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-[#1A1D2E]">
                            Désactiver cet utilisateur ?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-[#6B7280]">
                            {confirmDeactivate ? (
                                <span data-testid="user-deactivate-message">
                                    Êtes-vous sûr de vouloir désactiver <strong className="text-[#1A1D2E]">{confirmDeactivate.user.display_name}</strong> ?
                                    Il perdra immédiatement l'accès à Nivo.
                                </span>
                            ) : null}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy} data-testid="user-deactivate-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="user-deactivate-confirm"
                            disabled={busy}
                            onClick={confirmDeactivateNow}
                            className="bg-[#EF4444] text-white hover:bg-[#DC2626] focus:ring-[#EF4444]"
                        >
                            Désactiver
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function Th({ children, className = '' }) {
    return <th className={`text-left px-3 py-2 font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = '', ...rest }) {
    return <td className={`px-3 py-3 text-[#1A1D2E] align-middle ${className}`} {...rest}>{children}</td>;
}
